import React, { useState, useContext, useEffect } from "react";
import { getReserveList, getReservePosition } from "../../helpers/squad";
import SquadItem from "./item";
import styles from "./index.module.scss";
import { Button } from "react-bootstrap";
import { ATTACKER, DEFENDER, GOALKEEPER, MIDFIELDER } from "../../constants";
import Modal from "react-bootstrap/Modal";
import { getPlayerById, isPlayerExist } from "../../helpers/player";
import { SquadContext } from "../../providers/SquadProvider";
import { AdminContext } from "../../providers/AdminProvider";
import { SettingsContext } from "../../providers/SettingsProvider";
import isEqual from "lodash.isequal";
import { getPlayerShortTeam, getShortPosition } from "../../helpers/player";
import FootballField from "../FootballField";
import { parseScheme } from "../../helpers/scheme";
import { getReserveIndex } from "../FootballField";
import SchemePicker from "../SchemePicker";

export const isGoalKeeperSelected = (selectedItem, idx) => {
  if (selectedItem === null) return;
  const { position, index, isReserve } = selectedItem;
  return position === GOALKEEPER && index === idx && !isReserve;
};

export const isDefenderSelected = (selectedItem, idx) => {
  if (selectedItem === null) return;
  const { position, index, isReserve } = selectedItem;
  return position === DEFENDER && index === idx && !isReserve;
};

export const isMidFielderSelected = (selectedItem, idx) => {
  if (selectedItem === null) return;
  const { position, index, isReserve } = selectedItem;
  return position === MIDFIELDER && index === idx && !isReserve;
};

export const isAttackerSelected = (selectedItem, idx) => {
  if (selectedItem === null) return;
  const { position, index, isReserve } = selectedItem;
  return position === ATTACKER && index === idx && !isReserve;
};

export const isReserveSelected = (selectedItem, idx, reservePosition) => {
  if (selectedItem === null) return;
  const { position, index, isReserve } = selectedItem;
  return position === reservePosition && index === idx && isReserve;
};

export const isCapitan = (squad, playerId) => {
  return squad?.cap === playerId;
};

const Squad = ({ squad, onShowPlayerSearch, viewToggle, onShowPlayerInfo }) => {
  const [showModal, setShowModal] = useState(false);
  const [player, setPlayer] = useState(null);

  const {
    setAction,
    setOutPlayer,
    outPlayer,
    setInPlayer,
    selectedItem,
    setSelectedItem,
    setCapitan,
    removePlayer,
    clearAction,
    action,
    allowedTransfers,
  } = useContext(SquadContext);

  const { activeDeadline } = useContext(SettingsContext);

  const { players, setSearchPosition } = useContext(AdminContext);

  useEffect(() => {
    if (!selectedItem) return;
    const newPlayer = getPlayerById(players, selectedItem.playerId);
    setPlayer(newPlayer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem]);

  const sellPlayer = () => {
    removePlayer(outPlayer);
    setShowModal(false);
    clearAction();
  };

  const substitutePlayer = () => {
    setAction("substitute");
    setShowModal(false);
  };

  const makeCapitan = () => {
    setCapitan(outPlayer);
    setShowModal(false);
    clearAction();
  };

  const onItemClick = (options) => {
    const { playerId, isFantomPlayer } = options;

    if (!activeDeadline && !isPlayerExist(playerId)) return;

    if (!activeDeadline) {
      if (!isFantomPlayer) {
        onShowPlayerInfo(options.playerId);
      }
      return;
    }

    if (isPlayerExist(playerId) && !isFantomPlayer) {
      if (!outPlayer) {
        setShowModal(true);
        setSelectedItem({ ...options });
        setOutPlayer({ ...options });
      } else if (action === "substitute") {
        setInPlayer({ ...options });
      }
    } else {
      setAction("add");
      setSelectedItem({ ...options });
      setOutPlayer({ ...options });
      onShowPlayerSearch();
      setSearchPosition(options.position);
    }

    if (isEqual(options, selectedItem)) {
      setSelectedItem(null);
    }
  };

  const onHideActionsModal = () => {
    setShowModal(false);
    setSelectedItem(null);
    clearAction();
  };

  const showPlayerInfo = (playerId) => {
    onHideActionsModal();
    onShowPlayerInfo(playerId);
  };

  const isGoalkeeperClickAllowed =
    !action || (action === "substitute" && outPlayer.position === GOALKEEPER);
  const isDefenderClickAllowed =
    !action || (action === "substitute" && outPlayer.position === DEFENDER);

  const isMidFielderClickAllowed =
    !action || (action === "substitute" && outPlayer.position === MIDFIELDER);

  const isAttackerClickAllowed =
    !action || (action === "substitute" && outPlayer.position === ATTACKER);

  const isReservedClickAllowed = (index, outPlayer, action) => {
    return (
      !action ||
      (action === "substitute" &&
        outPlayer.position === getReservePosition(index))
    );
  };

  const reserveList = getReserveList(squad);

  return (
    <>
      <h5
        className={`${styles.titleRow} ${viewToggle ? styles.contrast : null}`}
      >
        <span>
          <i className="bi bi-person-fill"></i>&nbsp;
          {viewToggle ? "Склад" : "Стартовий склад"}
        </span>
        <SchemePicker active={activeDeadline} zeroTop={viewToggle} />
      </h5>
      {!viewToggle ? (
        <FootballField
          squad={squad}
          onItemClick={onItemClick}
          isGoalkeeperClickAllowed={isGoalkeeperClickAllowed}
          isDefenderClickAllowed={isDefenderClickAllowed}
          isMidfielderClickAllowed={isMidFielderClickAllowed}
          isAttackerClickAllowed={isAttackerClickAllowed}
          isReservedClickAllowed={isReservedClickAllowed}
          action={action}
          selectedItem={selectedItem}
          outPlayer={outPlayer}
        />
      ) : (
        <div>
          <div style={{ marginTop: 8 }}>
            <h5>
              <i className="bi bi-person-fill"></i>&nbsp;Голкіпер
            </h5>
            {squad.main["GK"].map((player, index) => (
              <SquadItem
                key={index}
                playerId={player}
                position={GOALKEEPER}
                index={index}
                isReserve={false}
                selected={isGoalKeeperSelected(selectedItem, index)}
                onItemClick={onItemClick}
                isClickAllowed={isGoalkeeperClickAllowed}
                isSubstitution={action === "substitute"}
                isCapitan={isCapitan(squad, player)}
              />
            ))}
          </div>
          <div>
            <h5>
              <i className="bi bi-person-fill"></i>&nbsp;Захисники
            </h5>
            {squad.main["D"].map((player, index) => (
              <SquadItem
                key={index}
                playerId={player}
                position={DEFENDER}
                index={index}
                isReserve={false}
                selected={isDefenderSelected(selectedItem, index)}
                onItemClick={onItemClick}
                isClickAllowed={isDefenderClickAllowed}
                isSubstitution={action === "substitute"}
                isCapitan={isCapitan(squad, player)}
              />
            ))}
          </div>
          <div>
            <h5>
              <i className="bi bi-person-fill"></i>&nbsp;Півзахисники
            </h5>
            {squad.main["MD"].map((player, index) => (
              <SquadItem
                key={index}
                playerId={player}
                position={MIDFIELDER}
                index={index}
                isReserve={false}
                selected={isMidFielderSelected(selectedItem, index)}
                onItemClick={onItemClick}
                isClickAllowed={isMidFielderClickAllowed}
                isSubstitution={action === "substitute"}
                isCapitan={isCapitan(squad, player)}
              />
            ))}
          </div>
          <div>
            <h5>
              <i className="bi bi-person-fill"></i>&nbsp;Нападники
            </h5>
            {squad.main["ST"].map((player, index) => (
              <SquadItem
                key={index}
                playerId={player}
                position={ATTACKER}
                index={index}
                isReserve={false}
                selected={isAttackerSelected(selectedItem, index)}
                onItemClick={onItemClick}
                isClickAllowed={isAttackerClickAllowed}
                isSubstitution={action === "substitute"}
                isCapitan={isCapitan(squad, player)}
              />
            ))}
          </div>
          <div>
            <h5>
              <i className="bi bi-person-fill"></i>&nbsp;Запасні
            </h5>
            {reserveList?.map((player, index) => {
              const scheme = parseScheme(squad);
              const position = getReservePosition(scheme, index);
              const playerIndex = getReserveIndex(
                player,
                squad?.reserve,
                position
              );
              return (
                <SquadItem
                  key={index}
                  playerId={player}
                  position={position}
                  index={playerIndex}
                  isReserve={true}
                  selected={isReserveSelected(
                    selectedItem,
                    playerIndex,
                    position
                  )}
                  onItemClick={onItemClick}
                  isClickAllowed={
                    !action ||
                    (action === "substitute" && outPlayer.position === position)
                  }
                  isSubstitution={action === "substitute"}
                  isCapitan={isCapitan(squad, player)}
                />
              );
            })}
          </div>
        </div>
      )}
      <Modal
        size="sm"
        show={showModal}
        onHide={onHideActionsModal}
        aria-labelledby="example-modal-sizes-title-sm"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title id="example-modal-sizes-title-sm">
            <div className={styles.photoCnt}>
              <img className={styles.photo} src={player?.photo} alt="" />
              <div className={styles.info}>
                <div className={styles.name}>
                  <div>{player?.FirstName}</div>
                  <div>{player?.LastName}</div>
                </div>
                <div className={styles.hint}>
                  {getPlayerShortTeam(player)} |{" "}
                  {getShortPosition(player?.position)}
                </div>
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <Button
              variant="success"
              className={styles.action}
              onClick={substitutePlayer}
            >
              <div className={styles.btnContent}>
                <i className="bi bi-arrow-repeat"></i>&nbsp;Замінити
              </div>
            </Button>
          </div>
          <div>
            {allowedTransfers > 0 && (
              <Button
                variant="danger"
                className={styles.action}
                onClick={sellPlayer}
              >
                <div className={styles.btnContent}>
                  <i className="bi bi-bag"></i>&nbsp;Продати
                </div>
              </Button>
            )}
          </div>

          <div>
            {!isCapitan(squad, selectedItem?.playerId) &&
              !selectedItem?.isReserve && (
                <Button
                  variant="warning"
                  className={styles.action}
                  onClick={makeCapitan}
                >
                  <div className={styles.btnContent}>
                    <i className="bi bi-bookmark-check-fill"></i>
                    &nbsp;Призначити капітаном
                  </div>
                </Button>
              )}
          </div>

          <div>
            <Button
              variant="info"
              className={styles.action}
              onClick={() => showPlayerInfo(player.PlayerId)}
            >
              <div className={styles.btnContent}>
                <i className="bi bi-info-circle"></i>&nbsp;Інформація
              </div>
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Squad;
